import { parseJpNCountSku } from '@entities/products/model/jp-n-count'
import {
  curriculumTypeSchema,
  langTypeSchema,
  lessonCountPerMonthSchema,
  lessonTimeSchema,
  paymentTypeSchema,
  promotionTypeSchema,
} from '@server/presentation'
import {
  jpNotificationAccountParamsSchema,
  notificationEntityIdSchema,
} from '@shared/models/jp-notification-destination'
import { z } from 'zod'

const 홈_화면 = 'HOME' as const

// 레슨 탭
const 레슨탭_화면 = 'LESSON_TAB' as const
const 체험레슨_화면 = 'TRIAL_LESSON' as const
const 정규레슨_화면 = 'REGULAR_LESSON' as const

/** @deprecated backward compatibility를 위함 */
const 영어_수업_화면 = 'LESSON_ENGLISH' as const
/** @deprecated backward compatibility를 위함 */
const 일본어_수업_화면 = 'LESSON_JAPANESE' as const

// 상품 목록
const 체험_상품_구독_목록_화면 = 'TRIAL_TICKET_LIST' as const
const 정기_구독_상품_목록_화면 = 'SUBSCRIBE_TICKET_LIST' as const
const 한번에_결제_상품_목록_화면 = 'LUMPSUM_TICKET_LIST' as const
const 비지니스_영어_상품_목록_화면 = 'BUSINESS_ENGLISH_TICKET_LIST' as const
const 아이패드_결제_상품_목록_화면 = 'IPAD_TICKET_LIST' as const
const 페이백_결제_상품_목록_화면 = 'PAYBACK_TICKET_LIST' as const
const 라이트_구독_상품_목록_화면 = 'LIGHT_SUBSCRIBE_TICKET_LIST' as const

// 마이포도
const 마이포도_화면 = 'MY_PODO' as const
const 마이포도_플랜_화면 = 'MY_PODO_PLAN' as const
const 마이_쿠폰_화면 = 'MY_COUPON' as const
const 고객센터_티켓_화면 = 'CS_TICKET' as const

const 진단_보고서_화면 = 'DIAGNOSIS_REPORT' as const

// 스마트톡 구매 페이지
const 스마트톡_구매_화면 = 'SMART_TALK_TICKET_LIST' as const
const 스마트톡_체험_구매_화면 = 'SMART_TALK_TRIAL_TICKET_LIST' as const

// 파트너스 사이트
const 파트너스_사이트 = 'PARTNERS' as const

// AI 학습
const AI_학습_화면 = 'AI_LEARNING' as const

// 예약 화면
const 예약_화면 = 'RESERVATION' as const
const 온보딩_시작_화면 = 'ONBOARDING_START' as const
const 일본_체험레슨_신청_화면 = 'TRIAL_APPLICATION' as const
const 일본_결제_화면 = 'JP_CHECKOUT' as const
const 일본_결제_결과_화면 = 'JP_PAYMENT_RETURN' as const

export const onboardingEntrySourceSchema = z.enum(['intro', 'mypage_reentry', 'legacy_trial'])

export const destinationSchema = z.enum([
  /** @description Home screen */
  홈_화면,
  /** @description Lesson tab */
  레슨탭_화면,
  /** @deprecated */
  영어_수업_화면,
  /** @deprecated */
  일본어_수업_화면,
  /** @description Trial lesson screen */
  체험레슨_화면,
  /** @description Regular lesson screen */
  정규레슨_화면,
  /** @description Trial ticket list screen */
  체험_상품_구독_목록_화면,
  /** @description Subscription ticket list screen */
  정기_구독_상품_목록_화면,
  /** @description Lump-sum ticket list screen */
  한번에_결제_상품_목록_화면,
  /** @description Business English ticket list screen */
  비지니스_영어_상품_목록_화면,
  /** @description iPad ticket list screen */
  아이패드_결제_상품_목록_화면,
  /** @description My PODO screen */
  마이포도_화면,
  /** @description My PODO plan screen */
  마이포도_플랜_화면,
  /** @description My coupon screen */
  마이_쿠폰_화면,
  /** @description Customer center ticket screen */
  고객센터_티켓_화면,
  /** @description Diagnosis report screen */
  진단_보고서_화면,
  /** @description Smart Talk purchase screen */
  스마트톡_구매_화면,
  /** @description Smart Talk trial purchase screen */
  스마트톡_체험_구매_화면,
  /** @description Payback ticket list screen */
  페이백_결제_상품_목록_화면,
  /** @description Light subscription (secret link) ticket list screen */
  라이트_구독_상품_목록_화면,
  /** @description Partners site */
  파트너스_사이트,
  /** @description AI learning screen */
  AI_학습_화면,
  /** @description Reservation screen */
  예약_화면,
  /** @description Onboarding start screen */
  온보딩_시작_화면,
  /** @description Japan trial lesson application screen */
  일본_체험레슨_신청_화면,
  /** @description Japan checkout screen */
  일본_결제_화면,
  /** @description Japan payment result screen */
  일본_결제_결과_화면,
  'KR_PAYMENT_RETURN',
  'PURCHASE_FLOW',
])
export type Destination = z.infer<typeof destinationSchema>

// 홈
export const homeDestinationSchema = z.object({
  destination: z.literal(destinationSchema.Enum.HOME),
  redirection: z.enum(['trial', 'payment', 'ticket']).optional(),
})

// 레슨탭
export const lessonTabDestinationSchema = z.object({
  destination: z.literal(destinationSchema.Enum.LESSON_TAB),
  langType: langTypeSchema,
})

/** @deprecated backward compatibility를 위함 */
export const lessonEnglishDestinationSchema = z.object({
  destination: z.literal(destinationSchema.Enum.LESSON_ENGLISH),
})

/** @deprecated backward compatibility를 위함 */
export const lessonJapaneseDestinationSchema = z.object({
  destination: z.literal(destinationSchema.Enum.LESSON_JAPANESE),
})

export const trialLessonDestinationSchema = z.object({
  destination: z.literal(destinationSchema.Enum.TRIAL_LESSON),
  langType: langTypeSchema,
})

export const regularLessonDestinationSchema = z.object({
  destination: z.literal(destinationSchema.Enum.REGULAR_LESSON),
  langType: langTypeSchema,
  lessonTime: z.string().transform((val) => Number(val)),
  level: z.string().transform((val) => Number(val)),
  curriculumType: curriculumTypeSchema.extract(['BASIC', 'BUSINESS']),
  classCourseId: z
    .string()
    .transform((val) => Number(val))
    .optional(),
})

// 체험수업 구독 목록
export const trialTicketListDestinationSchema = z.object({
  destination: z.literal(destinationSchema.Enum.TRIAL_TICKET_LIST),
})
// 정기구독 구매
export const subscribeTicketListDestinationSchema = z.object({
  destination: z.literal(destinationSchema.Enum.SUBSCRIBE_TICKET_LIST),
  paymentType: z.literal(paymentTypeSchema.Enum.SUBSCRIBE).optional(),
  langType: langTypeSchema.optional(),
  lessonTime: lessonTimeSchema.optional(),
  lessonCountPerMonth: lessonCountPerMonthSchema.optional(),
  enable: z.string().optional(),
})

// 한번에결제 구매
export const lumpSumTicketListDestinationSchema = z.object({
  destination: z.literal(destinationSchema.Enum.LUMPSUM_TICKET_LIST),
  paymentType: z.literal(paymentTypeSchema.Enum.LUMP_SUM),
  langType: langTypeSchema.optional(),
  lessonTime: lessonTimeSchema.optional(),
  lessonCountPerMonth: lessonCountPerMonthSchema.optional(),
  enable: z.string().optional(),
})

export const businessEnglishTicketListDestinationSchema = z.object({
  destination: z.literal(destinationSchema.Enum.BUSINESS_ENGLISH_TICKET_LIST),
  // TODO: 비지니스 영어 구매 타입 확인 필요
  paymentType: z.literal(paymentTypeSchema.Enum.LUMP_SUM),
  langType: langTypeSchema.optional(),
  lessonTime: lessonTimeSchema.optional(),
  lessonCountPerMonth: lessonCountPerMonthSchema.optional(),
})

// 아이패드 패키지 구매
export const ipadTicketListDestinationSchema = z.object({
  destination: z.literal(destinationSchema.Enum.IPAD_TICKET_LIST),
  paymentType: z.literal(paymentTypeSchema.Enum.IPAD),
  langType: langTypeSchema.optional(),
  lessonTime: lessonTimeSchema.optional(),
  lessonCountPerMonth: lessonCountPerMonthSchema.optional(),
})

// 마이포도
export const myPodoDestinationSchema = jpNotificationAccountParamsSchema.extend({
  destination: z.literal(destinationSchema.Enum.MY_PODO),
})
export const myPodoPlanDestinationSchema = z.object({ destination: z.literal(destinationSchema.Enum.MY_PODO_PLAN) })
export const myCouponDestinationSchema = z.object({
  destination: z.literal(destinationSchema.Enum.MY_COUPON),
  tabID: z.enum(['available', 'history']).optional().default('available'),
})
export const csTicketDestinationSchema = z.object({ destination: z.literal(destinationSchema.Enum.CS_TICKET) })

export const diagnosisReportDestinationSchema = z.object({
  destination: z.literal(destinationSchema.Enum.DIAGNOSIS_REPORT),
  classId: z.string().transform((val) => Number(val)),
})

// 스마트톡 구매
export const smartTalkDestinationSchema = z.object({
  destination: z.literal(destinationSchema.Enum.SMART_TALK_TICKET_LIST),
})

export const smartTalkTrialDestinationSchema = z.object({
  destination: z.literal(destinationSchema.Enum.SMART_TALK_TRIAL_TICKET_LIST),
})

// 페이백 결제 상품 목록
export const paybackTicketListDestinationSchema = z.object({
  destination: z.literal(destinationSchema.Enum.PAYBACK_TICKET_LIST),
  paymentType: z.literal(paymentTypeSchema.Enum.LUMP_SUM),
  langType: langTypeSchema.optional(),
  lessonTime: lessonTimeSchema.optional(),
  lessonCountPerMonth: lessonCountPerMonthSchema.optional(),
  promotionType: z.literal(promotionTypeSchema.Enum.PAYBACK).optional(),
  enable: z.string().optional(),
})

// 라이트 구독 시크릿 상품 목록 (/subscribes/tickets/light)
export const lightSubscribeTicketListDestinationSchema = z.object({
  destination: z.literal(destinationSchema.Enum.LIGHT_SUBSCRIBE_TICKET_LIST),
  langType: langTypeSchema.optional(),
})

// 파트너스 사이트
export const partnersSiteDestinationSchema = z.object({
  destination: z.literal(destinationSchema.Enum.PARTNERS),
})

// AI 학습
export const aiLearningDestinationSchema = z.object({
  destination: z.literal(destinationSchema.Enum.AI_LEARNING),
})

// 예약 화면
export const reservationDestinationSchema = z.object({
  destination: z.literal(destinationSchema.Enum.RESERVATION),
  enterClassId: notificationEntityIdSchema.optional(),
})

export const onboardingStartDestinationSchema = z.object({
  destination: z.literal(destinationSchema.Enum.ONBOARDING_START),
  source: onboardingEntrySourceSchema.optional(),
})

export const trialApplicationDestinationSchema = z.object({
  destination: z.literal(destinationSchema.Enum.TRIAL_APPLICATION),
  langType: z.string().max(16).optional(),
  LANG_TYPE: z.string().max(16).optional(),
})

const jpRecurringCheckoutSkuSchema = z.enum([
  'pass8-en-m1',
  'pass8-ko-m1',
  'pass8-both-m1',
  'pass8-en-m3',
  'pass8-ko-m3',
  'pass8-both-m3',
  'daily1-en-m1',
  'daily1-ko-m1',
  'daily1-both-m1',
  'daily1-en-m3',
  'daily1-ko-m3',
  'daily1-both-m3',
])
export const jpCheckoutSkuSchema = z.union([
  jpRecurringCheckoutSkuSchema,
  z
    .string()
    .max(256)
    .refine((value) => parseJpNCountSku(value) !== null),
])

export const jpCheckoutDestinationSchema = z.object({
  purchase_flow: z.string().uuid().optional(),
  destination: z.literal(destinationSchema.Enum.JP_CHECKOUT),
  sku: jpCheckoutSkuSchema,
  locale: z.enum(['ja', 'ko']).optional().default('ja'),
  from: z.enum(['plans', 'home', 'mypage']).optional().default('plans'),
  coupon_id: z.string().max(64).optional(),
  orderId: z.string().max(64).optional(),
  result: z.enum(['failed', 'retryable']).optional(),
})

export const jpPaymentReturnDestinationSchema = z.object({
  purchase_flow: z.string().uuid().optional(),
  destination: z.literal(destinationSchema.Enum.JP_PAYMENT_RETURN),
  orderId: z.string().min(1).max(64),
  sku: jpCheckoutSkuSchema.optional(),
  productId: z.string().min(1).max(64).optional(),
  code: z.string().max(128).optional(),
  message: z.string().max(256).optional(),
  pgCode: z.string().max(128).optional(),
  pgMessage: z.string().max(256).optional(),
  txId: z.string().max(128).optional(),
})

export const purchaseFlowDestinationSchema = z.object({
  destination: z.literal('PURCHASE_FLOW'),
  purchase_flow: z.string().uuid(),
})

export const krPaymentReturnDestinationSchema = z.object({
  destination: z.literal('KR_PAYMENT_RETURN'),
  paymentReturnContext: z.string().min(1).max(20_000),
})

export type HomeDestinationSchema = z.infer<typeof homeDestinationSchema>
export type LessonTabDestinationSchema = z.infer<typeof lessonTabDestinationSchema>

/** @deprecated backward compatibility를 위함 */
export type LessonEnglishDestinationSchema = z.infer<typeof lessonEnglishDestinationSchema>
/** @deprecated backward compatibility를 위함 */
export type LessonJapaneseDestinationSchema = z.infer<typeof lessonJapaneseDestinationSchema>
export type TrialLessonDestinationSchema = z.infer<typeof trialLessonDestinationSchema>
export type RegularLessonDestinationSchema = z.infer<typeof regularLessonDestinationSchema>
export type TrialTicketListDestinationSchema = z.infer<typeof trialTicketListDestinationSchema>
export type SubscribeTicketListDestinationSchema = z.infer<typeof subscribeTicketListDestinationSchema>
export type LumpSumTicketListDestinationSchema = z.infer<typeof lumpSumTicketListDestinationSchema>
export type BusinessEnglishTicketListDestinationSchema = z.infer<typeof businessEnglishTicketListDestinationSchema>
export type IpadTicketListDestinationSchema = z.infer<typeof ipadTicketListDestinationSchema>

export type MyPodoDestinationSchema = z.infer<typeof myPodoDestinationSchema>
export type MyPodoPlanDestinationSchema = z.infer<typeof myPodoPlanDestinationSchema>
export type MyCouponDestinationSchema = z.infer<typeof myCouponDestinationSchema>
export type CsTicketDestinationSchema = z.infer<typeof csTicketDestinationSchema>

export type DiagnosisReportDestinationSchema = z.infer<typeof diagnosisReportDestinationSchema>
export type SmartTalkDestinationSchema = z.infer<typeof smartTalkDestinationSchema>
export type SmartTalkTrialDestinationSchema = z.infer<typeof smartTalkTrialDestinationSchema>
export type PaybackTicketListDestinationSchema = z.infer<typeof paybackTicketListDestinationSchema>
export type LightSubscribeTicketListDestinationSchema = z.infer<typeof lightSubscribeTicketListDestinationSchema>
export type PartnersSiteDestinationSchema = z.infer<typeof partnersSiteDestinationSchema>
export type AiLearningDestinationSchema = z.infer<typeof aiLearningDestinationSchema>
export type ReservationDestinationSchema = z.infer<typeof reservationDestinationSchema>
export type OnboardingStartDestinationSchema = z.infer<typeof onboardingStartDestinationSchema>
export type TrialApplicationDestinationSchema = z.infer<typeof trialApplicationDestinationSchema>
export type JpCheckoutDestinationSchema = z.infer<typeof jpCheckoutDestinationSchema>
export type JpPaymentReturnDestinationSchema = z.infer<typeof jpPaymentReturnDestinationSchema>

export type GatewaySearchParams =
  | HomeDestinationSchema
  | LessonTabDestinationSchema
  /** @deprecated backward compatibility를 위함 */
  | LessonEnglishDestinationSchema
  /** @deprecated backward compatibility를 위함 */
  | LessonJapaneseDestinationSchema
  | TrialTicketListDestinationSchema
  | SubscribeTicketListDestinationSchema
  | LumpSumTicketListDestinationSchema
  | BusinessEnglishTicketListDestinationSchema
  | IpadTicketListDestinationSchema
  | MyPodoDestinationSchema
  | MyCouponDestinationSchema
  | MyPodoPlanDestinationSchema
  | CsTicketDestinationSchema
  | PaybackTicketListDestinationSchema
  | LightSubscribeTicketListDestinationSchema
  | PartnersSiteDestinationSchema
  | AiLearningDestinationSchema
  | OnboardingStartDestinationSchema
  | TrialApplicationDestinationSchema
  | JpCheckoutDestinationSchema
  | JpPaymentReturnDestinationSchema
/**
 * 목적지별 파라미터 매핑
 * 각 목적지에 해당하는 스키마 타입을 매핑합니다.
 */
export type DestinationParamsMap = {
  HOME: HomeDestinationSchema
  LESSON_TAB: LessonTabDestinationSchema
  /** @deprecated backward compatibility를 위함 */
  LESSON_ENGLISH: LessonEnglishDestinationSchema
  /** @deprecated backward compatibility를 위함 */
  LESSON_JAPANESE: LessonJapaneseDestinationSchema
  TRIAL_LESSON: TrialLessonDestinationSchema
  REGULAR_LESSON: RegularLessonDestinationSchema
  TRIAL_TICKET_LIST: TrialTicketListDestinationSchema
  SUBSCRIBE_TICKET_LIST: SubscribeTicketListDestinationSchema
  LUMPSUM_TICKET_LIST: LumpSumTicketListDestinationSchema
  BUSINESS_ENGLISH_TICKET_LIST: BusinessEnglishTicketListDestinationSchema
  IPAD_TICKET_LIST: IpadTicketListDestinationSchema
  MY_PODO: MyPodoDestinationSchema
  MY_PODO_PLAN: MyPodoPlanDestinationSchema
  MY_COUPON: MyCouponDestinationSchema
  CS_TICKET: CsTicketDestinationSchema
  DIAGNOSIS_REPORT: DiagnosisReportDestinationSchema
  SMART_TALK_TICKET_LIST: SmartTalkDestinationSchema
  SMART_TALK_TRIAL_TICKET_LIST: SmartTalkTrialDestinationSchema
  PAYBACK_TICKET_LIST: PaybackTicketListDestinationSchema
  LIGHT_SUBSCRIBE_TICKET_LIST: LightSubscribeTicketListDestinationSchema
  PARTNERS: PartnersSiteDestinationSchema
  AI_LEARNING: AiLearningDestinationSchema
  RESERVATION: ReservationDestinationSchema
  ONBOARDING_START: OnboardingStartDestinationSchema
  TRIAL_APPLICATION: TrialApplicationDestinationSchema
  JP_CHECKOUT: z.infer<typeof jpCheckoutDestinationSchema>
  JP_PAYMENT_RETURN: z.infer<typeof jpPaymentReturnDestinationSchema>
  PURCHASE_FLOW: z.infer<typeof purchaseFlowDestinationSchema>
  KR_PAYMENT_RETURN: z.infer<typeof krPaymentReturnDestinationSchema>
}

/**
 * 각 목적지별 스키마 매핑
 */
export const destinationSchemaMap = {
  HOME: homeDestinationSchema,
  LESSON_TAB: lessonTabDestinationSchema,
  /** @deprecated backward compatibility를 위함 */
  LESSON_ENGLISH: lessonEnglishDestinationSchema,
  /** @deprecated backward compatibility를 위함 */
  LESSON_JAPANESE: lessonJapaneseDestinationSchema,
  TRIAL_LESSON: trialLessonDestinationSchema,
  REGULAR_LESSON: regularLessonDestinationSchema,
  TRIAL_TICKET_LIST: trialTicketListDestinationSchema,
  SUBSCRIBE_TICKET_LIST: subscribeTicketListDestinationSchema,
  LUMPSUM_TICKET_LIST: lumpSumTicketListDestinationSchema,
  BUSINESS_ENGLISH_TICKET_LIST: businessEnglishTicketListDestinationSchema,
  IPAD_TICKET_LIST: ipadTicketListDestinationSchema,
  MY_PODO: myPodoDestinationSchema,
  MY_PODO_PLAN: myPodoPlanDestinationSchema,
  MY_COUPON: myCouponDestinationSchema,
  CS_TICKET: csTicketDestinationSchema,
  DIAGNOSIS_REPORT: diagnosisReportDestinationSchema,
  SMART_TALK_TICKET_LIST: smartTalkDestinationSchema,
  SMART_TALK_TRIAL_TICKET_LIST: smartTalkTrialDestinationSchema,
  PAYBACK_TICKET_LIST: paybackTicketListDestinationSchema,
  LIGHT_SUBSCRIBE_TICKET_LIST: lightSubscribeTicketListDestinationSchema,
  PARTNERS: partnersSiteDestinationSchema,
  AI_LEARNING: aiLearningDestinationSchema,
  RESERVATION: reservationDestinationSchema,
  ONBOARDING_START: onboardingStartDestinationSchema,
  TRIAL_APPLICATION: trialApplicationDestinationSchema,
  JP_CHECKOUT: jpCheckoutDestinationSchema,
  JP_PAYMENT_RETURN: jpPaymentReturnDestinationSchema,
  PURCHASE_FLOW: purchaseFlowDestinationSchema,
  KR_PAYMENT_RETURN: krPaymentReturnDestinationSchema,
} as const

/**
 * 목적지에 따른 추가 파라미터 타입을 추론합니다.
 */
export type GetParamsByDestination<DestinationK extends Destination> = Omit<
  DestinationParamsMap[DestinationK],
  'destination'
>
